import React from 'react'

import { Stack, Typography } from '@mui/material'

import { Skeleton } from '@repo/ui'

import { InfoTooltip } from '../../feedback/InfoTooltip'

interface AccessOverrideRollupProps {
  /** Products whose own column is non-null, i.e. that do not inherit. */
  products: number
  /**
   * `true` when the product count was taken over a capped page rather than the
   * whole catalog, so it is a FLOOR. Rendered as "at least N" — a capped count
   * presented as exact is worse than no count at all.
   */
  productsPartial?: boolean
  /**
   * Whether certificate-level overrides are included in this line. `false` today
   * (there is no server-side count), and the line says so rather than staying
   * silent — silence would read as "none", which is the same unfounded claim the
   * rollup exists to stop the page making.
   */
  certificatesCounted: boolean
  loading?: boolean
}

/**
 * VRG-591 — "does this toggle actually describe what we collect?", answered under
 * the toggle.
 *
 * ## The gap
 *
 * Settings -> Features shows the two certificate-access flags as switches, and a
 * switch reads as the whole answer. Once a product or a certificate can override
 * one, it is not: a brand can have "Require email to view" off at company level and
 * a dozen products quietly requiring it. Nothing anywhere reported that, and
 * nothing could — neither list surface has a filter for it — so "what do we
 * actually collect" was a question the product could not answer even in principle.
 * That matters more, not less, if per-region or per-drop overrides are used the way
 * they are intended to be.
 *
 * A count is the smallest thing that restores the answer. It is deliberately not a
 * link: filtering products or certificates by override is a real piece of work
 * (§46's `visible:` plumbing, a query param, a server filter on two list endpoints)
 * and half-built navigation is worse than none.
 *
 * ## Why it states its own incompleteness
 *
 * `certificatesCounted` is a prop and not an assumption. The line is read as an
 * inventory, and an inventory that silently omits a whole scope is a stronger claim
 * than no inventory. Naming the omission costs one clause and keeps the line
 * honest until the endpoint exists (see `useAccessOverrideCounts` for the shape it
 * should take).
 */
export const AccessOverrideRollup = ({
  products,
  productsPartial = false,
  certificatesCounted,
  loading,
}: AccessOverrideRollupProps) => {
  if (loading) {
    // §19 — a skeleton at the line's own footprint, not a spinner and not a blank,
    // so the card does not reflow when the count arrives.
    return <Skeleton width={180} height={16} />
  }

  /*
    Verb agreement, not just the noun. The first cut pluralised only the noun and
    shipped "1 product override this" — caught in the browser, because copy has no
    compiler. `At least 1` keeps the plural verb: the subject there is the phrase,
    and the count is a floor rather than a quantity.
  */
  const countText = productsPartial
    ? `At least ${products} ${products === 1 ? 'product' : 'products'} override this`
    : products === 0
      ? 'No products override this'
      : products === 1
        ? '1 product overrides this'
        : `${products} products override this`

  return (
    <Stack direction="row" alignItems="center" spacing={2}>
      <Typography variant="body3" color="text.secondary">
        {countText}
      </Typography>
      {!certificatesCounted && (
        /*
          A literal `text`, not a `term`. §44 sends a TERM OF ART to the glossary,
          and this is not one — it is a caveat about this build's coverage, true
          today and gone the moment the count endpoint lands. Putting a temporary
          limitation in the glossary would outlive the limitation.
        */
        <InfoTooltip
          text={
            'Individual certificates can override this too. Counting those needs a ' +
            'server-side query that does not exist yet, so they are not included in ' +
            'this number.'
          }
        />
      )}
    </Stack>
  )
}
